import { wrapCreateBrowserRouter } from '@sentry/react';
import { createBrowserRouter, type LoaderFunctionArgs, Navigate, Outlet, redirect } from 'react-router';

import { clientsQueryOptions } from '~/api/clients';
import { conditionsQueryOptions, customConditionsQueryOptions } from '~/api/conditions';
import { creditNoteFiltersQueryOptions, creditNotesQueryOptions, defaultCreditNoteQueryMetadata } from '~/api/creditNotes';
import { HttpUnauthenticatedError } from '~/api/errors';
import { forumPostsQueryOptions } from '~/api/forum/useForumPosts';
import {
  creditableInvoicesQueryOptions,
  defaultInvoiceQueryMetadata,
  invoiceFiltersQueryOptions,
  invoicesQueryOptions,
} from '~/api/invoices';
import {
  defaultInvoiceTemplateQueryMetadata,
  invoiceTemplateFiltersQueryOptions,
  invoiceTemplatesQueryOptions,
} from '~/api/invoiceTemplates';
import { productsQueryOptions } from '~/api/products';
import { defaultQuotationQueryMetadata, quotationFiltersQueryOptions, quotationsQueryOptions } from '~/api/quotations';
import { dashboardStatisticsQueryOptions, invitationsStatisticsQueryOptions, pricingStatisticsQueryOptions } from '~/api/statistics';
import { userQueryOptions } from '~/api/user';
import { LoadingScreen, Redirect } from '~/components';
import { env } from '~/constants/env';
import { SearchParamKeys } from '~/constants/url';
import { Layout } from '~/pages/_Layout/Layout';
import { Root } from '~/pages/_Root/Root';
import { ErrorBoundary as RootErrorBoundary } from '~/pages/_Root/Root.error';
import { Activate } from '~/pages/Auth/Activate/Activate';
import { AuthLayout } from '~/pages/Auth/AuthLayout/AuthLayout';
import { ForgotPassword } from '~/pages/Auth/ForgotPassword/ForgotPassword';
import { Login } from '~/pages/Auth/Login/Login';
import { Registration } from '~/pages/Auth/Registration/Registration';
import { RegistrationAccountInformation } from '~/pages/Auth/Registration/RegistrationAccountInformation';
import { RegistrationBusinessInformation } from '~/pages/Auth/Registration/RegistrationBusinessInformation';
import { RegistrationCompany } from '~/pages/Auth/Registration/RegistrationCompany';
import { RegistrationSuccess } from '~/pages/Auth/Registration/RegistrationSuccess';
import { RegistrationVatInformation } from '~/pages/Auth/Registration/RegistrationVatInformation';
import { ResetPasswordGuard } from '~/pages/Auth/ResetPassword/ResetPassword';
import { ClientDetailGuard } from '~/pages/Clients/ClientDetail/ClientDetail';
import { loader as clientDetailLoader } from '~/pages/Clients/ClientDetail/ClientDetail.loader';
import { ClientsOverview } from '~/pages/Clients/ClientsOverview/ClientsOverview';
import { ChooseClientType } from '~/pages/Clients/CreateClient/ChooseClientType/ChooseClientType';
import { CreateClient } from '~/pages/Clients/CreateClient/CreateClient';
import { CreateContactStep } from '~/pages/Clients/CreateClient/CreateContactStep/CreateContactStep';
import { CreateOrganisationStep } from '~/pages/Clients/CreateClient/CreateOrganisationStep/CreateOrganisationStep';
import { CreateContact } from '~/pages/Clients/CreateContact/CreateContact';
import { loader as createContactLoader } from '~/pages/Clients/CreateContact/CreateContact.loader';
import { EditClient } from '~/pages/Clients/EditClient/EditClient';
import { loader as editClientLoader } from '~/pages/Clients/EditClient/EditClient.loader';
import { EditContact } from '~/pages/Clients/EditContact/EditContact';
import { loader as editContactLoader } from '~/pages/Clients/EditContact/EditContact.loader';
import { NetworkOverview } from '~/pages/Community/NetworkOverview';
import { Dashboard } from '~/pages/Dashboard/Dashboard';
import { ErrorBoundary as DashboardErrorBoundary } from '~/pages/Dashboard/Dashboard.error';
import { ForumGuidelines } from '~/pages/Forum/ForumGuidelines/ForumGuidelines';
import { ForumLayout } from '~/pages/Forum/ForumLayout';
import { ErrorBoundary as ForumLayoutErrorBoundary } from '~/pages/Forum/ForumLayout.error';
import { ForumOverviewLayout } from '~/pages/Forum/ForumOverview/ForumOverviewLayout';
import { ForumSavedPosts } from '~/pages/Forum/ForumOverview/ForumSavedPosts';
import { ForumSelfPosts } from '~/pages/Forum/ForumOverview/ForumSelfPosts';
import { ForumTimeline } from '~/pages/Forum/ForumOverview/ForumTimeline';
import { ForumPostDetailGuard } from '~/pages/Forum/ForumPostDetail/ForumPostDetail';
import { ErrorBoundary as ForumPostDetailErrorBoundary } from '~/pages/Forum/ForumPostDetail/ForumPostDetail.error';
import { loader as forumPostLoader } from '~/pages/Forum/ForumPostDetail/ForumPostDetail.loader';
import { InvoicesTable } from '~/pages/InvoicesCreditnotes/components/InvoicesTable/InvoicesTable';
import { CreateCreditNoteForm } from '~/pages/InvoicesCreditnotes/CreateCreditNote/CreateCreditNoteForm';
import { ErrorBoundary as CreateCreditNoteErrorBoundary } from '~/pages/InvoicesCreditnotes/CreateCreditNote/CreateCreditNoteForm.error';
import { CreateInvoice } from '~/pages/InvoicesCreditnotes/CreateEditInvoice/CreateInvoice';
import { ErrorBoundary as CreateInvoiceErrorBoundary } from '~/pages/InvoicesCreditnotes/CreateEditInvoice/CreateInvoice.error';
import { loader as createInvoiceLoader } from '~/pages/InvoicesCreditnotes/CreateEditInvoice/CreateInvoice.loader';
import { EditInvoice } from '~/pages/InvoicesCreditnotes/CreateEditInvoice/EditInvoice';
import { ErrorBoundary as EditInvoiceErrorBoundary } from '~/pages/InvoicesCreditnotes/CreateEditInvoice/EditInvoice.error';
import { loader as editInvoiceLoader } from '~/pages/InvoicesCreditnotes/CreateEditInvoice/EditInvoice.loader';
import { CreditNotesTable } from '~/pages/InvoicesCreditnotes/CreditNotesOverview/CreditNotesTable';
import { InvoiceDetailGuard } from '~/pages/InvoicesCreditnotes/InvoiceDetail/InvoiceDetail';
import { ErrorBoundary as InvoiceDetailErrorBoundary } from '~/pages/InvoicesCreditnotes/InvoiceDetail/InvoiceDetail.error';
import { loader as invoiceDetailLoader } from '~/pages/InvoicesCreditnotes/InvoiceDetail/InvoiceDetail.loader';
import { InvoicesCreditNotesLayout } from '~/pages/InvoicesCreditnotes/InvoicesCreditNotesLayout';
import { CreateInvoiceTemplate } from '~/pages/InvoiceTemplates/CreateEditInvoiceTemplate/CreateInvoiceTemplate';
import { ErrorBoundary as CreateInvoiceTemplateErrorBoundary } from '~/pages/InvoiceTemplates/CreateEditInvoiceTemplate/CreateInvoiceTemplate.error';
import { EditInvoiceTemplate } from '~/pages/InvoiceTemplates/CreateEditInvoiceTemplate/EditInvoiceTemplate';
import { ErrorBoundary as EditInvoiceTemplateErrorBoundary } from '~/pages/InvoiceTemplates/CreateEditInvoiceTemplate/EditInvoiceTemplate.error';
import { loader as editInvoiceTemplateLoader } from '~/pages/InvoiceTemplates/CreateEditInvoiceTemplate/EditInvoiceTemplate.loader';
import { InvoiceTemplateDetailGuard } from '~/pages/InvoiceTemplates/InvoiceTemplateDetail/InvoiceTemplateDetail';
import { ErrorBoundary as InvoiceTemplateDetailErrorBoundary } from '~/pages/InvoiceTemplates/InvoiceTemplateDetail/InvoiceTemplateDetail.error';
import { loader as invoiceTemplateDetailLoader } from '~/pages/InvoiceTemplates/InvoiceTemplateDetail/InvoiceTemplateDetail.loader';
import { InvoiceTemplatesTable } from '~/pages/InvoiceTemplates/InvoiceTemplatesOverview/InvoiceTemplatesTable';
import { BookmarkedJobPosts } from '~/pages/JobPosts/BookmarkedJobPosts';
import { CreateJobPost } from '~/pages/JobPosts/CreateEditJobPost/CreateJobPost';
import { EditJobPost } from '~/pages/JobPosts/CreateEditJobPost/EditJobPost';
import { loader as editJobPostLoader } from '~/pages/JobPosts/CreateEditJobPost/EditJobPost.loader';
import { JobPostDetailGuard } from '~/pages/JobPosts/JobPostDetail/JobPostDetail';
import { loader as jobPostDetailLoader } from '~/pages/JobPosts/JobPostDetail/JobPostDetail.loader';
import { JobPosts } from '~/pages/JobPosts/JobPosts';
import { JobPostsOverviewLayout } from '~/pages/JobPosts/JobPostsOverviewLayout';
import { OwnJobPosts } from '~/pages/JobPosts/OwnJobPosts';
import { CreateEditProduct } from '~/pages/Products/CreateEditProduct/CreateEditProduct';
import { loader as createEditProductLoader } from '~/pages/Products/CreateEditProduct/CreateEditProduct.loader';
import { ProductsOverview } from '~/pages/Products/ProductsOverview/ProductsOverview';
import { QuotationForClient } from '~/pages/QuotationForClient/QuotationForClient';
import { ErrorBoundary as QuotationForClientErrorBoundary } from '~/pages/QuotationForClient/QuotationForClient.error';
import { loader as quotationForClientLoader } from '~/pages/QuotationForClient/QuotationForClient.loader';
import { QuotationForClientLayout } from '~/pages/QuotationForClient/QuotationForClientLayout';
import { CreateQuotation } from '~/pages/Quotations/CreateEditQuotation/CreateQuotation';
import { ErrorBoundary as CreateQuotationErrorBoundary } from '~/pages/Quotations/CreateEditQuotation/CreateQuotation.error';
import { loader as createQuotationLoader } from '~/pages/Quotations/CreateEditQuotation/CreateQuotation.loader';
import { EditQuotation } from '~/pages/Quotations/CreateEditQuotation/EditQuotation';
import { ErrorBoundary as EditQuotationErrorBoundary } from '~/pages/Quotations/CreateEditQuotation/EditQuotation.error';
import { loader as editQuotationLoader } from '~/pages/Quotations/CreateEditQuotation/EditQuotation.loader';
import { QuotationsOverview } from '~/pages/Quotations/QuotationsOverview/QuotationsOverview';
import { Company } from '~/pages/Settings/Company/Company';
import { Documents } from '~/pages/Settings/Documents/Documents';
import { Email } from '~/pages/Settings/Email/Email';
import { Integrations } from '~/pages/Settings/Integrations/Integrations';
import { OauthBillit } from '~/pages/Settings/Integrations/Oauth/OauthBillit';
import { Invitations } from '~/pages/Settings/Invitations/Invitations';
import { Invoice } from '~/pages/Settings/Invoice/Invoice';
import { Pricing } from '~/pages/Settings/Pricing/Pricing';
import { Profile } from '~/pages/Settings/Profile/Profile';
import { SettingsGeneralRedirect } from '~/pages/Settings/SettingsGeneralRedirect';
import { SettingsLayout } from '~/pages/Settings/SettingsLayout';
import { insertIf } from '~/utils/arrays';
import { qs } from '~/utils/searchParams';

import { queryClient } from '../QueryClientProvider/queryClient';
import { r, routes } from './router.routes';
import { SessionWatcher } from './SessionWatcher';

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    element: <Root />,
    shouldRevalidate: () => false,
    errorElement: <RootErrorBoundary />,
    hydrateFallbackElement: <LoadingScreen />,
    children: [
      // Auth routes
      {
        path: '/',
        element: (
          <AuthLayout>
            <Outlet />
          </AuthLayout>
        ),
        children: [
          {
            path: routes.login,
            element: <Login />,
          },
          {
            path: routes.register,
            element: <Registration />,
            children: [
              {
                index: true,
                element: <RegistrationVatInformation />,
              },
              {
                path: routes.registerAccountInformation,
                element: <RegistrationAccountInformation />,
              },
              {
                path: routes.registerBusinessInformation,
                element: <RegistrationBusinessInformation />,
              },
              {
                path: routes.registerCompany,
                element: <RegistrationCompany />,
              },
              {
                path: routes.registerSuccess,
                element: <RegistrationSuccess />,
              },
            ],
          },
          {
            path: routes.activate,
            element: <Activate />,
          },
          {
            path: routes.forgotPassword,
            element: <ForgotPassword />,
          },
          {
            path: routes.resetPassword,
            element: <ResetPasswordGuard />,
          },
        ],
      },

      // Authenticated routes
      {
        path: '/',
        // The loader is a one-time cache primer: it runs when entering the authenticated area and must not re-run on in-app navigations.
        // Revalidating would refetch the whole primed set on every search-param change and paint the global loading screen mid-session.
        shouldRevalidate: () => false,
        loader: async ({ request }: LoaderFunctionArgs) => {
          // Gate before priming anything else. Prefetching in parallel with the session check would fire
          // every request below on an unauthenticated cold load, each rejecting with its own 401.
          try {
            await queryClient.ensureQueryData(userQueryOptions);
          } catch (error) {
            if (!(error instanceof HttpUnauthenticatedError)) throw error;

            const { hash, pathname, search } = window.location;

            return redirect(`${routes.login}${qs({ [SearchParamKeys.REDIRECT_PATH]: `${pathname}${search}${hash}` })}`);
          }

          await Promise.all([
            // Critical, these queries are used as suspense queries. Prefetching them together ensures we can show
            // a single global loading state once, instead of triggering a suspense boundary each time a suspense query mounts.
            // If any of these queries fail, the component will throw and the error will be caught by the nearest error boundary.
            queryClient.prefetchQuery(creditableInvoicesQueryOptions),
            queryClient.prefetchQuery(quotationFiltersQueryOptions),
            queryClient.prefetchQuery(invoiceFiltersQueryOptions),
            queryClient.prefetchQuery(creditNoteFiltersQueryOptions),
            queryClient.prefetchQuery(invoiceTemplateFiltersQueryOptions),
            queryClient.prefetchQuery(productsQueryOptions),
            queryClient.prefetchQuery(clientsQueryOptions),
            queryClient.prefetchQuery(conditionsQueryOptions),
            queryClient.prefetchQuery(customConditionsQueryOptions),

            // Non-critical, don't waste time retrying on error.
            // If any of these queries fail, the error state will be handled inside the component(s) that use them.
            queryClient.prefetchQuery({ ...quotationsQueryOptions(defaultQuotationQueryMetadata), retry: false }),
            queryClient.prefetchQuery({ ...invoicesQueryOptions(defaultInvoiceQueryMetadata), retry: false }),
            queryClient.prefetchQuery({ ...creditNotesQueryOptions(defaultCreditNoteQueryMetadata), retry: false }),
            queryClient.prefetchQuery({ ...invoiceTemplatesQueryOptions(defaultInvoiceTemplateQueryMetadata), retry: false }),
            queryClient.prefetchQuery({ ...dashboardStatisticsQueryOptions, retry: false }),
          ]);

          // Delayed non-critical queries
          // If any of these queries fail, the error state will be handled inside the component(s) that use them.
          const timeoutId = setTimeout(() => {
            queryClient.prefetchQuery({ ...invitationsStatisticsQueryOptions, retry: false });
            queryClient.prefetchQuery({ ...pricingStatisticsQueryOptions, retry: false });
            queryClient.prefetchInfiniteQuery({ ...forumPostsQueryOptions(), retry: false });
          }, 2000);

          request.signal.addEventListener('abort', () => clearTimeout(timeoutId));
        },
        element: (
          <>
            <SessionWatcher />

            <Layout>
              <Outlet />
            </Layout>
          </>
        ),
        children: [
          {
            index: true,
            element: <Redirect to={routes.dashboard} />,
          },
          {
            path: routes.dashboard,
            element: <Dashboard />,
            errorElement: <DashboardErrorBoundary />,
          },
          {
            path: routes.quotations,
            element: <QuotationsOverview />,
            handle: { breadcrumb: () => ({ label: 'quotations:breadcrumb.overview' }) },
          },
          {
            path: routes.createQuotation,
            element: <CreateQuotation />,
            loader: createQuotationLoader,
            errorElement: <CreateQuotationErrorBoundary />,
            handle: {
              breadcrumb: () => [{ label: 'quotations:breadcrumb.overview', to: routes.quotations }, { label: 'common:breadcrumbs.draft' }],
            },
          },
          {
            path: routes.editQuotation,
            element: <EditQuotation />,
            loader: editQuotationLoader,
            errorElement: <EditQuotationErrorBoundary />,
            handle: {
              breadcrumb: (quotation?: Awaited<ReturnType<typeof editQuotationLoader>>) =>
                quotation
                  ? [
                      { label: 'quotations:breadcrumb.overview', to: routes.quotations },
                      quotation.title ? { literal: quotation.title } : { label: 'quotations:breadcrumb.noTitle' },
                      { label: 'common:actions.edit' },
                    ]
                  : [{ label: 'quotations:breadcrumb.overview', to: routes.quotations }, { label: 'common:actions.edit' }],
            },
          },
          {
            element: <InvoicesCreditNotesLayout />,
            children: [
              {
                path: routes.invoices,
                element: <InvoicesTable />,
                handle: { breadcrumb: () => ({ label: 'invoices:breadcrumb.overview' }) },
              },
              {
                path: routes.creditNotes,
                element: <CreditNotesTable />,
                handle: { breadcrumb: () => ({ label: 'creditnotes:breadcrumb.overview' }) },
              },
              {
                path: routes.invoiceTemplates,
                element: <InvoiceTemplatesTable />,
                handle: { breadcrumb: () => ({ label: 'invoiceTemplates:breadcrumb.overview' }) },
              },
            ],
          },
          {
            path: routes.showInvoice,
            element: <InvoiceDetailGuard />,
            loader: invoiceDetailLoader,
            errorElement: <InvoiceDetailErrorBoundary />,
            handle: {
              breadcrumb: (invoice?: Awaited<ReturnType<typeof invoiceDetailLoader>>) =>
                invoice
                  ? [
                      { label: 'invoices:breadcrumb.overview', to: routes.invoices },
                      invoice.title ? { literal: invoice.title } : { label: 'invoices:breadcrumb.noTitle' },
                    ]
                  : [{ label: 'invoices:breadcrumb.overview', to: routes.invoices }, { label: 'common:breadcrumbs.details' }],
            },
          },
          {
            path: routes.createInvoice,
            element: <CreateInvoice />,
            loader: createInvoiceLoader,
            errorElement: <CreateInvoiceErrorBoundary />,
            handle: {
              breadcrumb: () => [{ label: 'invoices:breadcrumb.overview', to: routes.invoices }, { label: 'common:breadcrumbs.draft' }],
            },
          },
          {
            path: routes.editInvoice,
            element: <EditInvoice />,
            loader: editInvoiceLoader,
            errorElement: <EditInvoiceErrorBoundary />,
            handle: {
              breadcrumb: (invoice?: Awaited<ReturnType<typeof editInvoiceLoader>>) =>
                invoice
                  ? [
                      { label: 'invoices:breadcrumb.overview', to: routes.invoices },
                      invoice.title
                        ? { literal: invoice.title, to: r(routes.showInvoice, { invoiceId: invoice.id }) }
                        : { label: 'invoices:breadcrumb.noTitle', to: r(routes.showInvoice, { invoiceId: invoice.id }) },
                      { label: 'common:actions.edit' },
                    ]
                  : [{ label: 'invoices:breadcrumb.overview', to: routes.invoices }, { label: 'common:actions.edit' }],
            },
          },
          {
            path: routes.createCreditNote,
            element: <CreateCreditNoteForm />,
            errorElement: <CreateCreditNoteErrorBoundary />,
            handle: {
              breadcrumb: () => [{ label: 'creditnotes:breadcrumb.overview', to: routes.creditNotes }, { label: 'common:actions.create' }],
            },
          },
          {
            path: routes.showInvoiceTemplate,
            element: <InvoiceTemplateDetailGuard />,
            loader: invoiceTemplateDetailLoader,
            errorElement: <InvoiceTemplateDetailErrorBoundary />,
            handle: {
              breadcrumb: (invoiceTemplate?: Awaited<ReturnType<typeof invoiceTemplateDetailLoader>>) =>
                invoiceTemplate
                  ? [
                      { label: 'invoiceTemplates:breadcrumb.overview', to: routes.invoiceTemplates },
                      invoiceTemplate.title ? { literal: invoiceTemplate.title } : { label: 'invoiceTemplates:breadcrumb.noTitle' },
                    ]
                  : [
                      { label: 'invoiceTemplates:breadcrumb.overview', to: routes.invoiceTemplates },
                      { label: 'common:breadcrumbs.details' },
                    ],
            },
          },
          {
            path: routes.createInvoiceTemplate,
            element: <CreateInvoiceTemplate />,
            errorElement: <CreateInvoiceTemplateErrorBoundary />,
            handle: {
              breadcrumb: () => [
                { label: 'invoiceTemplates:breadcrumb.overview', to: routes.invoiceTemplates },
                { label: 'common:actions.create' },
              ],
            },
          },
          {
            path: routes.editInvoiceTemplate,
            element: <EditInvoiceTemplate />,
            loader: editInvoiceTemplateLoader,
            errorElement: <EditInvoiceTemplateErrorBoundary />,
            handle: {
              breadcrumb: (invoiceTemplate?: Awaited<ReturnType<typeof editInvoiceTemplateLoader>>) =>
                invoiceTemplate
                  ? [
                      { label: 'invoiceTemplates:breadcrumb.overview', to: routes.invoiceTemplates },
                      invoiceTemplate.title
                        ? {
                            literal: invoiceTemplate.title,
                            to: r(routes.showInvoiceTemplate, { invoiceTemplateId: invoiceTemplate.id }),
                          }
                        : {
                            label: 'invoiceTemplates:breadcrumb.noTitle',
                            to: r(routes.showInvoiceTemplate, { invoiceTemplateId: invoiceTemplate.id }),
                          },
                      { label: 'common:actions.edit' },
                    ]
                  : [{ label: 'invoiceTemplates:breadcrumb.overview', to: routes.invoiceTemplates }, { label: 'common:actions.edit' }],
            },
          },
          {
            path: routes.products,
            element: <ProductsOverview />,
            handle: { breadcrumb: () => ({ label: 'products:breadcrumb.overview' }) },
          },
          {
            path: routes.createProduct,
            element: <CreateEditProduct />,
            handle: {
              breadcrumb: () => [{ label: 'products:breadcrumb.overview', to: routes.products }, { label: 'common:actions.create' }],
            },
          },
          {
            path: routes.editProduct,
            element: <CreateEditProduct />,
            loader: createEditProductLoader,
            handle: {
              breadcrumb: (data?: Awaited<ReturnType<typeof createEditProductLoader>>) => {
                const productName = data?.product?.name[data?.locale] ?? data?.product?.name.nl;

                if (!productName) return [{ label: 'products:breadcrumb.overview', to: routes.products }, { label: 'common:actions.edit' }];

                return [
                  { label: 'products:breadcrumb.overview', to: routes.products },
                  { literal: productName },
                  { label: 'common:actions.edit' },
                ];
              },
            },
          },
          {
            path: routes.clients,
            element: <ClientsOverview />,
            handle: { breadcrumb: () => ({ label: 'clients:breadcrumb.overview' }) },
          },
          {
            path: routes.showClient,
            element: <ClientDetailGuard />,
            loader: clientDetailLoader,
            handle: {
              breadcrumb: (client?: Awaited<ReturnType<typeof clientDetailLoader>>) => [
                { label: 'clients:breadcrumb.overview', to: routes.clients },
                client?.name ? { literal: client.name } : { label: 'common:breadcrumbs.details' },
              ],
            },
          },
          {
            path: routes.createClient,
            element: <ChooseClientType />,
            handle: {
              breadcrumb: () => [{ label: 'clients:breadcrumb.overview', to: routes.clients }, { label: 'common:actions.create' }],
            },
          },
          {
            path: routes.createClient,
            element: <CreateClient />,
            handle: {
              breadcrumb: () => [{ label: 'clients:breadcrumb.overview', to: routes.clients }, { label: 'common:actions.create' }],
            },
            children: [
              {
                path: routes.createClientOrganisationStep,
                element: <CreateOrganisationStep />,
              },
              {
                path: routes.createClientContactStep,
                element: <CreateContactStep />,
              },
              {
                path: routes.createClientPrivateStep,
                element: <CreateContactStep />,
              },
            ],
          },
          {
            path: routes.editClient,
            element: <EditClient />,
            loader: editClientLoader,
            handle: {
              breadcrumb: (client?: Awaited<ReturnType<typeof editClientLoader>>) =>
                client
                  ? [
                      { label: 'clients:breadcrumb.overview', to: routes.clients },
                      client.clientType === 'organisation'
                        ? { literal: client.name, to: r(routes.showClient, { clientId: client.id }) }
                        : { literal: client.name },
                      { label: 'common:actions.edit' },
                    ]
                  : [{ label: 'clients:breadcrumb.overview', to: routes.clients }, { label: 'common:actions.edit' }],
            },
          },
          {
            path: routes.createContact,
            element: <CreateContact />,
            loader: createContactLoader,
            handle: {
              breadcrumb: (client?: Awaited<ReturnType<typeof createContactLoader>>) =>
                client
                  ? [
                      { label: 'clients:breadcrumb.overview', to: routes.clients },
                      ...insertIf(client.clientType === 'organisation', {
                        literal: client.name,
                        to: r(routes.showClient, { clientId: client.id }),
                      }),
                      { label: 'clients:breadcrumb.createContact' },
                    ]
                  : [{ label: 'clients:breadcrumb.overview', to: routes.clients }, { label: 'clients:breadcrumb.createContact' }],
            },
          },
          {
            path: routes.editContact,
            element: <EditContact />,
            loader: editContactLoader,
            handle: {
              breadcrumb: ({ client, contact }: Awaited<ReturnType<typeof editContactLoader>> = { client: null, contact: null }) =>
                client && contact
                  ? [
                      { label: 'clients:breadcrumb.overview', to: routes.clients },
                      ...insertIf(client.clientType === 'organisation', {
                        literal: client.name,
                        to: r(routes.showClient, { clientId: client.id }),
                      }),
                      { literal: contact.fullName },
                      { label: 'common:actions.edit' },
                    ]
                  : [{ label: 'clients:breadcrumb.overview', to: routes.clients }, { label: 'common:actions.edit' }],
            },
          },
          {
            path: routes.network,
            element: <NetworkOverview />,
            handle: { breadcrumb: () => ({ label: 'community:breadcrumb.overview' }) },
          },
          {
            path: routes.forum,
            element: <ForumLayout />,
            errorElement: <ForumLayoutErrorBoundary />,
            children: [
              {
                index: true,
                element: <Redirect to={routes.forumTimeline} />,
              },
              {
                element: <ForumOverviewLayout />,
                children: [
                  {
                    path: routes.forumTimeline,
                    element: <ForumTimeline />,
                    handle: { breadcrumb: () => ({ label: 'forum:breadcrumb.root' }) },
                  },
                  {
                    path: routes.forumSelfPosts,
                    element: <ForumSelfPosts />,
                    handle: {
                      breadcrumb: () => [{ label: 'forum:breadcrumb.root', to: routes.forumTimeline }, { label: 'forum:breadcrumb.self' }],
                    },
                  },
                  {
                    path: routes.forumSavedPosts,
                    element: <ForumSavedPosts />,
                    handle: {
                      breadcrumb: () => [{ label: 'forum:breadcrumb.root', to: routes.forumTimeline }, { label: 'forum:breadcrumb.saved' }],
                    },
                  },
                ],
              },
              {
                path: routes.forumPost,
                element: <ForumPostDetailGuard />,
                loader: forumPostLoader,
                errorElement: <ForumPostDetailErrorBoundary />,
                handle: {
                  breadcrumb: (post?: Awaited<ReturnType<typeof forumPostLoader>>) => [
                    { label: 'forum:breadcrumb.root', to: routes.forumTimeline },
                    post ? { literal: post.title } : { label: 'common:breadcrumbs.details' },
                  ],
                },
              },
              {
                path: routes.forumGuidelines,
                element: <ForumGuidelines />,
                handle: {
                  breadcrumb: () => [
                    { label: 'forum:breadcrumb.root', to: routes.forumTimeline },
                    { label: 'forum:breadcrumb.guidelines' },
                  ],
                },
              },
              {
                path: '*',
                element: <Redirect to={routes.forumTimeline} />,
              },
            ],
          },
          {
            element: <JobPostsOverviewLayout />,
            children: [
              {
                path: routes.jobPosts,
                element: <JobPosts />,
                handle: { breadcrumb: () => ({ label: 'jobPosts:breadcrumb.overview' }) },
              },
              {
                path: routes.bookmarkedJobPosts,
                element: <BookmarkedJobPosts />,
                handle: {
                  breadcrumb: () => [
                    { label: 'jobPosts:breadcrumb.overview', to: routes.jobPosts },
                    { label: 'jobPosts:breadcrumb.bookmarked' },
                  ],
                },
              },
              {
                path: routes.ownJobPosts,
                element: <OwnJobPosts />,
                handle: {
                  breadcrumb: () => [{ label: 'jobPosts:breadcrumb.overview', to: routes.jobPosts }, { label: 'jobPosts:breadcrumb.own' }],
                },
              },
            ],
          },
          {
            path: routes.createJobPost,
            element: <CreateJobPost />,
            handle: {
              breadcrumb: () => [{ label: 'jobPosts:breadcrumb.overview', to: routes.jobPosts }, { label: 'common:actions.create' }],
            },
          },
          {
            path: routes.editJobPost,
            loader: editJobPostLoader,
            element: <EditJobPost />,
            handle: {
              breadcrumb: (jobPost?: Awaited<ReturnType<typeof editJobPostLoader>>) =>
                jobPost
                  ? [
                      { label: 'jobPosts:breadcrumb.overview', to: routes.jobPosts },
                      { literal: jobPost.title, to: r(routes.showJobPost, { jobPostId: jobPost.id }) },
                      { label: 'common:actions.edit' },
                    ]
                  : [{ label: 'jobPosts:breadcrumb.overview', to: routes.jobPosts }, { label: 'common:actions.edit' }],
            },
          },
          {
            path: routes.showJobPost,
            loader: jobPostDetailLoader,
            element: <JobPostDetailGuard />,
            handle: {
              breadcrumb: (jobPost?: Awaited<ReturnType<typeof jobPostDetailLoader>>) => [
                { label: 'jobPosts:breadcrumb.overview', to: routes.jobPosts },
                jobPost ? { literal: jobPost.title } : { label: 'common:breadcrumbs.details' },
              ],
            },
          },
          {
            element: <SettingsLayout />,
            handle: {
              breadcrumb: () => [{ label: 'settings:breadcrumb.root', to: routes.settingsProfile }],
            },
            children: [
              {
                path: routes.settingsProfile,
                element: <Profile />,
                handle: { breadcrumb: () => [{ label: 'settings:tabs.profile' }] },
              },
              {
                path: routes.settingsEmail,
                element: <Email />,
                handle: { breadcrumb: () => [{ label: 'settings:tabs.email' }] },
              },
              {
                path: routes.settingsInvoice,
                element: <Invoice />,
                handle: { breadcrumb: () => [{ label: 'settings:tabs.invoice' }] },
              },
              {
                path: routes.settingsCompany,
                element: <Company />,
                handle: { breadcrumb: () => [{ label: 'settings:tabs.company' }] },
              },
              {
                path: routes.settingsIntegrations,
                element: <Integrations />,
                handle: { breadcrumb: () => [{ label: 'settings:tabs.integrations' }] },
              },
            ],
          },
          {
            path: routes.invitations,
            element: <Invitations />,
            handle: {
              breadcrumb: () => [{ label: 'settings:breadcrumb.invitations' }],
            },
          },
          {
            path: routes.documents,
            element: <Documents />,
            handle: {
              breadcrumb: () => [{ label: 'settings:breadcrumb.documents' }],
            },
          },
          {
            path: routes.pricing,
            element: <Pricing />,
            handle: {
              breadcrumb: () => [{ label: 'settings:breadcrumb.pricing' }],
            },
          },
          {
            path: routes.oauthBillit,
            element: <OauthBillit />,
          },

          // Redirects for legacy routes
          { path: '/settings/invitations', element: <Redirect to={routes.invitations} /> },
          { path: '/settings/documents', element: <Redirect to={routes.documents} /> },
          { path: '/settings/pricing', element: <Redirect to={routes.pricing} /> },
          { path: '/settings/plan', element: <Redirect to={routes.pricing} /> },
          { path: '/settings/general', element: <SettingsGeneralRedirect /> },
        ],
      },

      // Non-authenticated routes
      {
        element: <QuotationForClientLayout />,
        children: [
          {
            path: routes.quotationForClient,
            element: <QuotationForClient />,
            loader: quotationForClientLoader,
            errorElement: <QuotationForClientErrorBoundary />,
          },
        ],
      },

      // Design system
      ...insertIf(!env.IS_PRODUCTION_ENV, {
        path: routes.designSystem,
        lazy: {
          Component: async () => (await import('~/pages/DesignSystem/DesignSystem')).DesignSystem,
        },
      }),

      // Redirect
      {
        path: '*',
        element: <Navigate replace to={routes.dashboard} />,
      },
    ],
  },
]);
